import React from 'react';
import * as PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import FetchData, { Props as FetchDataProps, Fetch, Render, Transform } from 'lib/fetch-data/fetch-data';
import ForceUpdate from 'lib/fetch-data/force-update';
import Styled from 'styled';

import PagerView from 'four-nets/ui/pager-view';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import { UserStore, User } from 'four-nets/user/user';
import { Site } from 'four-nets/site';
import { FourNetsApi } from 'four-nets/api/api';
import { PagedResult } from 'lib/api/django-restframework/paged-result/paged-result';
import { Review, ReviewStore } from 'four-nets/bazaar/bazaar';
import ReviewView from 'four-nets/bazaar/review/review-view';

import defaultStyles from './reviews-view.scss';

interface Props {
    user?: User | { username: string };
    page: number;
    fromSellers: boolean;
    onPage: any;
    styles?: typeof defaultStyles;
}

interface Data {
    reviewStore: ReviewStore;
    reviews: PagedResult<Review>;
    fromSellers: boolean;
    onPage: any;
    forceUpdate: () => void;
}

interface FetchProps extends Props, FetchDataProps<Data, Data> {
    user: User;
    forceUpdate: () => void;
}

class ReviewLoader extends React.Component<Props> {
    static contextTypes = {
        api: PropTypes.instanceOf(FourNetsApi),
        userStore: PropTypes.instanceOf(UserStore),
        site: PropTypes.instanceOf(Site),
    };

    fetch: Fetch<Data, FetchProps> = (setData, props) => {
        const fetchData = this.createFetchData();

        const { fromSellers, page, user } = props;
        return fetchData.reviewStore
            .loadReviews(user.username, fromSellers, page)
            .then((reviews: PagedResult<Review>) => {
                setData({
                    reviews,
                    fromSellers,
                    onPage: props.onPage,
                    reviewStore: fetchData.reviewStore,
                    forceUpdate: props.forceUpdate,
                });
            });
    };

    createFetchData = () => {
        const { api, userStore } = this.context;
        const reviewStore = new ReviewStore({ api, userStore });
        return { reviewStore };
    };

    transform: Transform<Data, Data, FetchProps> = (data, props) => {
        const fetchData = this.createFetchData();
        const reviews = fetchData.reviewStore.processJSONArray({ json: data.reviews });
        return {
            reviews,
            fromSellers: data.fromSellers,
            onPage: data.onPage,
            reviewStore: data.reviewStore,
            forceUpdate: props.forceUpdate,
        };
    };

    renderFetch: Render<Data> = ({ loading, error, data }) => (
        <LoadingWrapperView loading={loading || !data}>
            {data &&
                data.reviews.results.map(review => (
                    <ReviewView
                        key={review.id}
                        review={review}
                        fromSellers={this.props.fromSellers}
                        forceUpdate={data.forceUpdate}
                    />
                ))}
            {data && data.reviews.results.length === 0 ? (
                <div className={this.props.styles!.empty}>
                    <FormattedMessage id='TH_SELLER_HAS_NO_REVIEWS' />
                </div>
            ) : null}
            {data && data.reviews ? (
                <PagerView
                    current={data.reviews.page}
                    count={data.reviews.pages}
                    onPage={data.onPage}
                    linkify={false}
                />
            ) : null}
        </LoadingWrapperView>
    );

    render() {
        return (
            <ForceUpdate>
                {({ forceUpdate }: { forceUpdate: () => void }) => (
                    <FetchData
                        serializerKey={__filename}
                        fetch={this.fetch}
                        transform={this.transform}
                        render={this.renderFetch}
                        forceUpdate={forceUpdate}
                        user={this.props.user || this.context.userStore.me}
                        page={this.props.page}
                        fromSellers={this.props.fromSellers}
                        onPage={this.props.onPage}
                    />
                )}
            </ForceUpdate>
        );
    }
}

export default Styled(defaultStyles)(ReviewLoader);
